import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Header,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useState } from 'react';
import { useUser } from '@/hooks/user-user';
import { LoadingSpinner } from '../StyledHtmlElements/Button';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
}

export function DataTable<TData, TValue>({ columns, data, loading }: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { isGlobalAdmin } = useUser();
  const table = useReactTable({
    data,
    columns: columns.map((col) => ({ ...col, enableSorting: col.enableSorting ?? false })),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility: {
        isGlobalAdmin: isGlobalAdmin,
      },
    },
    onSortingChange: setSorting,
  });

  const getSortIcon = (header: Header<TData, unknown>) => {
    if (!header.column.getCanSort()) return null;

    const icons = {
      asc: '↑',
      desc: '↓',
      false: '↕',
    };
    const sortState = header.column.getIsSorted() as keyof typeof icons;
    return icons[sortState] || null;
  };

  return (
    <div className="rounded-md border h-full overflow-auto  relative">
      <table className="w-full">
        <TableHeader className="sticky top-0 z-10 bg-background">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer select-none">
                    <div className="flex items-center">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      <span className="ml-1 text-xs mb-1">{getSortIcon(header)}</span>
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {loading ? (
                  <div className="w-full flex items-center justify-center">
                    <LoadingSpinner size={24} className={' text-primaryColor'} />
                  </div>
                ) : (
                  'Keine Daten vorhanden'
                )}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
}
